import { getToken } from '@/utils/auth';
import {
  UpdateUserLoanInfoRequest,
  UpdateUserRequest,
  User,
} from '@/types/UserType';
import { rtkApi } from '../rtkApi';

export const userApi = rtkApi.injectEndpoints({
  endpoints: (builder) => ({
    /** No HTTP call when logged out — avoids 401 spam and logout interceptor on public pages. */
    getCurrentUser: builder.query<User | null, void>({
      async queryFn(_arg, _api, _extraOptions, baseQuery) {
        if (!getToken()) {
          return { data: null };
        }
        const res = await baseQuery({ url: `/user`, method: 'GET' });
        if (res && 'error' in res && res.error) {
          return { error: res.error };
        }
        return { data: res.data as User };
      },
      providesTags: ['User'],
    }),
    updateUser: builder.mutation<void, UpdateUserRequest>({
      query: (request) => ({
        url: `/user`,
        method: 'PUT',
        data: request,
      }),
      invalidatesTags: ['User'],
    }),
    updateUserLoanInfo: builder.mutation<void, UpdateUserLoanInfoRequest>({
      query: (request) => ({
        url: `/user/loaninfo`,
        method: 'PUT',
        data: request,
      }),
      invalidatesTags: ['User'],
    }),
    sendApproveRequest: builder.mutation<void, void>({
      query: () => ({
        url: `/user/approve`,
        method: 'POST',
      }),
      invalidatesTags: ['User'],
    }),
    getUserInterestRate: builder.query<number, void>({
      query: () => ({ url: `/global/interest-rate` }),
    }),
    getLenderDefaults: builder.query<
      {
        lenderName: string;
        lenderAddress: string;
        lenderNMLS: string;
        loanOfficerName: string;
        loanOfficerPhone: string;
        loanOfficerEmail: string;
        loanOfficerNMLS: string;
      },
      void
    >({
      query: () => ({ url: `/global/lender-defaults` }),
      transformResponse: (response: {
        success: boolean;
        data: {
          lenderName: string;
          lenderAddress: string;
          lenderNMLS: string;
          loanOfficerName: string;
          loanOfficerPhone: string;
          loanOfficerEmail: string;
          loanOfficerNMLS: string;
        };
      }) => response.data,
    }),
    addDocument: builder.mutation<
      void,
      | string
      | { document: string; requirementId?: string; requirementKey?: string }
    >({
      query: (arg) => {
        const body =
          typeof arg === 'string'
            ? { document: arg }
            : {
                document: arg.document,
                requirementId: arg.requirementId,
                requirementKey: arg.requirementKey,
              };
        return {
          url: `/user/document`,
          method: 'POST',
          data: body,
        };
      },
      invalidatesTags: ['User', 'DocumentRequirements'],
    }),
    removeDocument: builder.mutation<void, string>({
      query: (document) => ({
        url: `/user/document`,
        method: 'DELETE',
        params: { document },
      }),
      invalidatesTags: ['User', 'DocumentRequirements'],
    }),
    removeUser: builder.mutation<void, void>({
      query: () => ({
        url: `/user`,
        method: 'DELETE',
      }),
      invalidatesTags: ['User'],
    }),
    deleteUserData: builder.mutation<void, string>({
      query: (email) => ({
        url: `/user/data`,
        params: { email },
        method: 'DELETE',
      }),
      invalidatesTags: ['User'],
    }),
    verifyDeleteRequest: builder.mutation<void, string>({
      query: (uid: string) => ({
        url: '/user/data/verify',
        method: 'post',
        data: { uid },
      }),
      invalidatesTags: ['User'],
    }),
  }),
});

export const {
  useGetCurrentUserQuery,
  useUpdateUserMutation,
  useUpdateUserLoanInfoMutation,
  useSendApproveRequestMutation,
  useGetUserInterestRateQuery,
  useGetLenderDefaultsQuery,
  useAddDocumentMutation,
  useRemoveDocumentMutation,
  useRemoveUserMutation,
  useDeleteUserDataMutation,
  useVerifyDeleteRequestMutation,
} = userApi;
